import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import { useVisualElement } from 'hooks/queries/visualElements/useVisualElement'
import Styles from './Templates.module.scss'
import { getTemplatePreviewCloudinaryUrl } from 'components/Design/utils/getTemplatePreviewCloudinaryUrl'
import { capitalize, startCase } from 'lodash'
import { createTLStore, Editor, TLShapeUtilConstructor } from '@tldraw/tldraw'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { useEffect, useRef, useState } from 'react'
import { getColors } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { BaseColorSwatch } from 'components/Design/ColorSwatch/ColorSwatch'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ContentType } from 'components/ContentType/ContentType'
import { Skeleton } from 'antd'
import cx from 'classnames'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { WIDE_ASPECT_RATIO } from 'components/Wizards/Wizard/Form/TemplateSelect/TemplateSelect'
import { AspectRatioKeyMenu } from './TemplatesModalV2/components/AspectRatioKeyMenu'
import { TemplateModalProps } from './Types'
import { TemplatesVideoIcon } from './TemplatesVidoIcon/TemplatesVideoIcon'
import { Check2, Close, LinkAngled } from 'blaze-ui/icons'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { VisualElement } from 'api/clients/VisualElementClient'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

interface UseThisTemplateProps extends Pick<TemplateModalProps, 'onConfirm'> {
  aspectRatioKeyForWizard: AspectRatioKey
  templateId: number
}

const UseThisTemplate = ({ onConfirm, aspectRatioKeyForWizard, templateId }: UseThisTemplateProps) => {
  const { isMobile } = useResponsiveDesign()
  const { width, height } = ASPECT_RATIOS[aspectRatioKeyForWizard]
  const { closeModal } = useModals()

  return (
    <ConditionalWrapper
      condition={!onConfirm}
      wrapper={(children) => (
        <ContentType
          type={aspectRatioKeyForWizard}
          initialValues={{ source_visual_element_id: templateId, startWithTemplate: true, width, height }}
        >
          {children}
        </ContentType>
      )}
    >
      <Button
        fullWidth={isMobile}
        size={isMobile ? 'xl' : 'md'}
        onPress={() => {
          if (onConfirm) {
            onConfirm(aspectRatioKeyForWizard)
          } else {
            closeModal()
          }
        }}
      >
        Use This Template
      </Button>
    </ConditionalWrapper>
  )
}

interface TemplatePreviewProps {
  template: VisualElement
  setIsLoading: (loading: boolean) => void
}

const TemplatePreview = ({ template, setIsLoading }: TemplatePreviewProps) => {
  const { isMobile } = useResponsiveDesign()
  const image = new Image()
  image.onload = () => setIsLoading(false)
  image.src = getTemplatePreviewCloudinaryUrl(template.preview_url, { width: isMobile ? 345 : 476 })

  return (
    <>
      <img src={image.src} className={Styles.templatePreview} alt={template.name} />
      <Skeleton active paragraph={false} className={Styles.skeleton} />
      {template.types.includes('video') && <TemplatesVideoIcon size="lg" />}
    </>
  )
}

const CopyButton = () => {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(window.location.href) // eslint-disable-line no-restricted-properties
  }

  return (
    <>
      <ResponsiveDesign.Mobile>
        <IconButton variant="ghost" onPress={handleCopy} icon={() => <LinkAngled size={24} />} aria-label="Copy Link" />
      </ResponsiveDesign.Mobile>
      <ResponsiveDesign.Desktop>
        <Button size="md" onPress={handleCopy} variant="tertiary">
          Copy Link
        </Button>
      </ResponsiveDesign.Desktop>
    </>
  )
}

export const TemplateModal = ({
  initialTemplateId,
  onPressOutside,
  initialAspectRatioKey,
  isAspectRatioKeyEditable,
  onConfirm,
  onClose,
  content,
  onChangeTemplate: handleChangeTemplate,
  isSharingAllowed,
}: TemplateModalProps) => {
  const [templateId, setTemplateId] = useState(initialTemplateId)
  const { data: template } = useVisualElement({ id: templateId })
  const { isMobile } = useResponsiveDesign()

  const tlStore = useRef(createTLStore({ shapeUtils }))

  if (template) {
    tlStore.current.loadSnapshot(template.content.snapshot)
  }

  const editor = useRef(
    new Editor({
      store: tlStore.current,
      shapeUtils: shapeUtils as TLShapeUtilConstructor<any, any>[],
      tools: [],
      getContainer: () => document.createElement('div'),
    }),
  )

  useEffect(() => editor.current.dispose(), [])
  const [isLoading, setIsLoading] = useState(true)
  const [aspectRatioKeyForWizard, setAspectRatioKeyForWizard] = useState(initialAspectRatioKey)

  if (!template) {
    return null
  }

  const { name, icon: Icon, width, height } = ASPECT_RATIOS[aspectRatioKeyForWizard]
  const colors = [...getColors(editor.current)]
  const isWide = width / height > WIDE_ASPECT_RATIO

  return (
    <Modal.Root
      onPressOutside={onPressOutside}
      size="md"
      aria-labelledby="dialog-heading"
      height={isWide ? 600 : undefined}
    >
      <ResponsiveDesign.Desktop>
        <Modal.Header>
          <div className={Styles.modalHeading}>
            <Icon size={32} />
            <Heading level={3} id="dialog-heading" lineClamp={1}>
              {template.name}
            </Heading>
          </div>
          <div className={Styles.actions}>
            {isSharingAllowed && <CopyButton />}
            <UseThisTemplate
              aspectRatioKeyForWizard={aspectRatioKeyForWizard}
              templateId={template.id}
              onConfirm={onConfirm}
            />
          </div>
        </Modal.Header>
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        {isSharingAllowed && (
          <Modal.Header onClose={onClose} actions={<CopyButton />}>
            <Heading level={5} lineClamp={1}>
              {template.name}
            </Heading>
          </Modal.Header>
        )}
        {!isSharingAllowed && <Modal.Header onClose={onClose} title={template.name} />}
      </ResponsiveDesign.Mobile>
      <Modal.Content>
        <div className={cx(Styles.modalContent, { [Styles.loading]: isLoading })}>
          <div className={Styles.previewContainer} style={{ aspectRatio: width / height }}>
            {!!content && <DesignContentPreview content={content} />}
            {!content && <TemplatePreview template={template} setIsLoading={setIsLoading} />}
          </div>
          <ResponsiveDesign.Mobile>
            <div className={Styles.buttonContainer}>
              <UseThisTemplate
                aspectRatioKeyForWizard={aspectRatioKeyForWizard}
                templateId={template.id}
                onConfirm={onConfirm}
              />
            </div>
          </ResponsiveDesign.Mobile>
          <div className={Styles.detailsCard}>
            {!isAspectRatioKeyEditable && (
              <div className={Styles.detailsHeading}>{isMobile ? template.name : name}</div>
            )}
            <div className={Styles.details}>
              {isAspectRatioKeyEditable && (
                <div className={Styles.detail}>
                  <div className={Styles.detailLabel}>Format</div>
                  <AspectRatioKeyMenu
                    initialTemplateId={templateId}
                    initialAspectRatioKey={aspectRatioKeyForWizard}
                    onChange={(aspectRatioKey, templateId) => {
                      setIsLoading(true)
                      setTemplateId(templateId)
                      setAspectRatioKeyForWizard(aspectRatioKey)
                      handleChangeTemplate?.(aspectRatioKey, templateId)
                    }}
                    templateBinderId={template.template_binder_id}
                  />
                </div>
              )}
              {template.category && (
                <div className={Styles.detail}>
                  <div className={Styles.detailLabel}>Category</div>
                  <div className={Styles.detailValue}>{template.category}</div>
                </div>
              )}
              {template.types.length > 0 && (
                <div className={Styles.detail}>
                  <div className={Styles.detailLabel}>Type</div>
                  <div className={Styles.detailsContainer}>
                    {template.types.map((type) => (
                      <div key={type} className={Styles.detailValue}>
                        {startCase(type)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {template.style.length > 0 && (
                <div className={Styles.detail}>
                  <div className={Styles.detailLabel}>Style</div>
                  <div className={Styles.detailsContainer}>
                    {template.style.map((style) => (
                      <div key={style} className={Styles.detailValue}>
                        {capitalize(style)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={Styles.detail}>
                <div className={Styles.detailLabel}>Replaceable image</div>
                <div className={cx(Styles.detailValue, Styles.detailValueIconContainer)}>
                  {template.has_replaceable && <Check2 aria-label="Has a replaceable image" />}
                  {!template.has_replaceable && <Close aria-label="Does not have a replaceable image" />}
                </div>
              </div>
              {colors.length > 0 && (
                <div className={Styles.detail}>
                  <div className={Styles.detailLabel}>Colors</div>
                  <div className={Styles.colors}>
                    {colors.map((color, index) => (
                      <BaseColorSwatch key={index} color={color} size={32} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
